import React, { useEffect, useRef, useState } from "react";
import { DownloadIcon } from "./common/Icons";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/adminDataModal.css";
import {
  PRICE_FIELDS,
  calculateProductPrices,
  cleanNumericValue,
} from "../../utils/PriceCalculator";

export interface FormField {
  key: string;
  label: string;
  type?:
    | "text"
    | "textarea"
    | "number"
    | "currency"
    | "password"
    | "select"
    | "multiselect"
    | "date"
    | "image"
    | "video"
    | "color-variants"
    | "checkbox-group"
    | "permission-matrix"
    | "repeatable-textarea"
    | "repeatable-faq"
    | "repeatable-solution"
    | "readonly-text"
    | "repeatable-card-textarea"
    | "repeatable-card-faq"
    | "repeatable-combo-list";
  imageType?: "single" | "multiple" | "product";
  imageFolder?:
    | "users"
    | "subscribers"
    | "products"
    | "brands"
    | "subcategories"
    | "promotions"
    | "testimonials"
    | "about"
    | "latestnews"
    | "career"
    | "team"
    | "software"
    | "software-modules"
    | "software-support";
  uploadField?: string;
  options?: { label: string; value: any; [key: string]: any }[];
  disabled?: boolean;
  colSpan?: 1 | 2 | 3 | 4;
  showWhen?: {
    key: string;
    value: any | any[];
  };
}

interface AdminDataModalProps {
  isOpen: boolean;
  mode: "add" | "edit" | "view";
  title: string;
  fields: FormField[];
  rowData?: any;
  disabled?: string;
  onClose: () => void;
  onSubmit?: (formData: any) => void;
  onFormChange?: (formData: any) => void;
  size?: "md" | "lg" | "xl";
  footer?: React.ReactNode;
  hideDefaultSubmit?: boolean;
  modalClassName?: string;
  bodyClassName?: string;
}

const buildFormDataFromRow = (rowData: any, fields: FormField[]) => {
  const updatedData: any = { ...(rowData || {}) };

  fields.forEach((field) => {
    /* =========================
       PERMISSION MATRIX
    ========================= */
    if (field.type === "permission-matrix") {
      const rights: any = {};
      const modules = rowData?.modules || rowData?.rights || [];

      if (Array.isArray(modules)) {
        modules.forEach((r: any) => {
          rights[r.module] = {
            view: r.can_view === 1,
            create: r.can_create === 1,
            edit: r.can_edit === 1,
            delete: r.can_delete === 1,
          };
        });
      }

      updatedData[field.key] = rights;
    }

    /* =========================
   MULTI SELECT
   ========================= */
    if (field.type === "multiselect") {
      const value = rowData?.[field.key];

      if (Array.isArray(value)) {
        updatedData[field.key] = value
          .map((item: any) => {
            if (typeof item === "object" && item !== null) {
              return Number(item.id || item.value);
            }

            return Number(item);
          })
          .filter(Boolean);
      } else if (typeof value === "string" && value.trim()) {
        try {
          const parsed = JSON.parse(value);

          updatedData[field.key] = Array.isArray(parsed)
            ? parsed.map((id: any) => Number(id)).filter(Boolean)
            : [];
        } catch {
          updatedData[field.key] = value
            .split(",")
            .map((id: any) => Number(id))
            .filter(Boolean);
        }
      } else if (value) {
        updatedData[field.key] = [Number(value)].filter(Boolean);
      } else {
        updatedData[field.key] = [];
      }
    }

    /* =========================
       SINGLE IMAGE
    ========================= */
    if (
      field.type === "image" &&
      field.imageType === "single" &&
      field.imageFolder
    ) {
      const fileName = rowData?.[field.key];

      if (
        fileName &&
        typeof fileName === "string" &&
        updatedData[field.key] !== null &&
        updatedData[field.key] !== undefined
      ) {
        updatedData[field.key] = {
          preview: `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/${field.imageFolder}/${fileName}`,
        };
      }
    }

    /* =========================
       PRODUCT IMAGES
    ========================= */
    if (
      field.type === "image" &&
      field.imageType === "product" &&
      field.imageFolder
    ) {
      const imageArray = rowData?.images;

      if (Array.isArray(imageArray) && rowData?.id) {
        const formattedImages = [...imageArray]
          .sort((a: any, b: any) => {
            if (Number(b.is_main) !== Number(a.is_main)) {
              return Number(b.is_main) - Number(a.is_main);
            }
            return Number(a.id) - Number(b.id);
          })
          .map((img: any) => ({
            preview: `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${rowData.id}/${img.image_path}`,
            id: img.id,
            is_main: img.is_main,
          }));

        updatedData[field.key] = formattedImages;
      }
    }

    /* =========================
   COLOR VARIANTS
   ========================= */
    if (field.type === "color-variants") {
      const variants = rowData?.[field.key];

      updatedData[field.key] =
        Array.isArray(variants) && variants.length > 0
          ? variants.map((variant: any) => ({
              ...variant,
              color: variant.color || variant.color_name || "",
              images:
                Array.isArray(variant.images) && variant.images.length > 0
                  ? variant.images.map((img: any) => ({
                      id: img.id,
                      image_path: img.image_path,
                      preview: `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${rowData.id}/${img.image_path}`,
                    }))
                  : [null, null, null],
            }))
          : [{ color: "", images: [null, null, null] }];
    }

    /* =========================
   VIDEO
   ========================= */
    if (field.type === "video") {
      const fileName = rowData?.[field.key];

      if (fileName && typeof fileName === "string") {
        updatedData[field.key] = {
          preview: `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${rowData.id}/${fileName}`,
          fileName,
        };
      } else {
        updatedData[field.key] = "";
      }
    }
  });

  return updatedData;
};

const buildEmptyFormData = (fields: FormField[]) => {
  const emptyData: any = {};

  fields.forEach((col) => {
    if (col.key === "status") {
      emptyData[col.key] = "active";
    } else {
      emptyData[col.key] = "";
    }
  });

  return emptyData;
};

const AdminDataModal: React.FC<AdminDataModalProps> = ({
  isOpen,
  mode,
  title,
  fields,
  rowData,
  onClose,
  onSubmit,
  onFormChange,
  size,
  footer,
  hideDefaultSubmit,
  modalClassName = "",
  bodyClassName = "",
}) => {
  const [formData, setFormData] = useState<any>({});
  const skipFirstFormChangeSync = useRef(true);
  const { showModal } = useMessageModal();
  const [multiSelectSearch, setMultiSelectSearch] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (skipFirstFormChangeSync.current) {
      skipFirstFormChangeSync.current = false;
      return;
    }

    onFormChange?.(formData);
  }, [formData, onFormChange]);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" || mode === "view") {
      skipFirstFormChangeSync.current = true;
      setFormData(buildFormDataFromRow(rowData, fields));
    } else {
      skipFirstFormChangeSync.current = true;
      setFormData(buildEmptyFormData(fields));
    }
  }, [
    isOpen,
    mode,
    rowData?.id,
    rowData?.updated_at,
    JSON.stringify(rowData?.images || []),
  ]);

  if (!isOpen) return null;

  const toggleColumnPermission = (
    key: string,
    permission: string,
    checked: boolean,
    modules: any[],
  ) => {
    const current = formData[key] || {};
    const updated: any = { ...current };

    modules.forEach((module) => {
      const moduleData = updated[module.value] || {};

      updated[module.value] = {
        ...moduleData,
        [permission]: checked,
      };
    });

    const nextFormData = {
      ...formData,
      [key]: updated,
    };

    setFormData(nextFormData);
  };

  // const cleanNumericValue = (value: any) => {
  //   if (value === null || value === undefined || value === "") return "";

  //   return String(value)
  //     .replace(/RM/gi, "")
  //     .replace(/MYR/gi, "")
  //     .replace(/,/g, "")
  //     .trim();
  // };

  // const handleChange = (key: string, value: any) => {
  //   setFormData((prev: any) => {
  //     const updated = {
  //       ...prev,
  //       [key]: value,
  //     };

  //     // Clean price/currency fields before storing in formData
  //     const priceFields = [
  //       "cost",
  //       "actualPrice",
  //       "previousPrice",
  //       "sellingPrice",
  //     ];

  //     if (priceFields.includes(key)) {
  //       const cleanedValue = cleanNumericValue(value);
  //       updated[key] = cleanedValue;
  //     }

  //     // Auto-calculate prices when cost changes
  //     if (key === "cost") {
  //       const cleanedCost = cleanNumericValue(value);
  //       const costValue = Number(cleanedCost) || 0;

  //       updated.cost = cleanedCost;
  //       updated.actualPrice = +(costValue * 1.4).toFixed(2); // +40%
  //       updated.previousPrice = +(costValue * 1.35).toFixed(2); // +35%
  //       updated.sellingPrice = +(costValue * 1.25).toFixed(2); // +25%
  //     }

  //     return updated;
  //   });
  // };

  const handleChange = (key: string, value: any) => {
    setFormData((prev: any) => {
      const updated = {
        ...prev,
        [key]: value,
      };

      if (PRICE_FIELDS.includes(key)) {
        updated[key] = cleanNumericValue(value);
      }

      if (key === "cost") {
        const calculatedPrices = calculateProductPrices(value);

        updated.cost = calculatedPrices.cleanedCost;
        updated.actualPrice = calculatedPrices.actualPrice;
        updated.previousPrice = calculatedPrices.previousPrice;
        updated.sellingPrice = calculatedPrices.sellingPrice;
      }

      return updated;
    });
  };

  const handleColorVariantImageChange = (
    key: string,
    variantIndex: number,
    imageIndex: number,
    files: FileList | null,
  ) => {
    if (!files) return;

    const file = files[0];
    const preview = URL.createObjectURL(file);

    setFormData((prev: any) => {
      const updatedVariants = [...(prev[key] || [])];
      const targetVariant = {
        ...(updatedVariants[variantIndex] || {
          color: "",
          images: [null, null, null],
        }),
      };

      const updatedImages = [...(targetVariant.images || [null, null, null])];
      updatedImages[imageIndex] = { file, preview };

      targetVariant.images = updatedImages;
      updatedVariants[variantIndex] = targetVariant;

      return {
        ...prev,
        [key]: updatedVariants,
      };
    });
  };

  const askRemoveColorVariantImage = (
    key: string,
    variantIndex: number,
    imageIndex: number,
  ) => {
    showModal({
      type: "confirm",
      title: "Remove Image",
      message: "Are you sure you want to remove this image?",
      confirmText: "Remove",
      cancelText: "Cancel",
      onConfirm: () => {
        setFormData((prev: any) => {
          const updatedVariants = [...(prev[key] || [])];
          const targetVariant = {
            ...(updatedVariants[variantIndex] || {
              color: "",
              images: [null, null, null],
            }),
          };

          const updatedImages = [
            ...(targetVariant.images || [null, null, null]),
          ];
          updatedImages[imageIndex] = undefined;

          targetVariant.images = updatedImages;
          updatedVariants[variantIndex] = targetVariant;

          return {
            ...prev,
            [key]: updatedVariants,
          };
        });

        const input = document.getElementById(
          `${key}-${variantIndex}-${imageIndex}`,
        ) as HTMLInputElement;

        if (input) input.value = "";
      },
    });
  };

  const handleImageChange = (key: string, files: FileList | null) => {
    if (!files) return;
    const file = files[0];
    handleChange(key, {
      file,
      preview: URL.createObjectURL(file),
    });
  };

  const handleProductImageChange = (
    key: string,
    index: number,
    files: FileList | null,
  ) => {
    if (!files) return;

    const file = files[0];
    const preview = URL.createObjectURL(file);

    const updatedImages = [...(formData[key] || [])];
    updatedImages[index] = { file, preview };

    const nextFormData = {
      ...formData,
      [key]: updatedImages,
    };

    setFormData(nextFormData);
  };

  const askRemoveSingleImage = (key: string) => {
    showModal({
      type: "confirm",
      title: "Remove Image",
      message: "Are you sure you want to remove this image?",
      confirmText: "Remove",
      cancelText: "Cancel",
      onConfirm: () => {
        const nextFormData = {
          ...formData,
          [key]: { removed: true },
        };

        setFormData(nextFormData);

        const input = document.getElementById(
          `${key}-single`,
        ) as HTMLInputElement;

        if (input) input.value = "";
      },
    });
  };

  const askRemoveProductImage = (key: string, index: number) => {
    showModal({
      type: "confirm",
      title: "Remove Image",
      message: "Are you sure you want to remove this image?",
      confirmText: "Remove",
      cancelText: "Cancel",
      onConfirm: () => {
        setFormData((prev: any) => {
          const updatedImages = [...(prev[key] || [])];
          updatedImages[index] = undefined;
          return { ...prev, [key]: updatedImages };
        });

        const input = document.getElementById(
          `${key}-${index}`,
        ) as HTMLInputElement;

        if (input) input.value = "";
      },
    });
  };

  const downloadImage = async (image: any, filename = "image") => {
    try {
      if (!image) return;

      const url = image.preview || image;
      const fileName = url.split("/").pop() || filename;

      const response = await fetch(url);
      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleCheckboxChange = (key: string, value: any) => {
    const current = formData[key] || [];

    const nextFormData = current.includes(value)
      ? {
          ...formData,
          [key]: current.filter((v: any) => v !== value),
        }
      : {
          ...formData,
          [key]: [...current, value],
        };

    setFormData(nextFormData);
  };

  const handleSubmit = () => {
    const submissionData = { ...formData };
    if (mode === "add") delete submissionData.id;
    onSubmit?.(submissionData);
  };

  const shouldShowField = (field: FormField) => {
    if (!field.showWhen) return true;

    const currentValue = formData[field.showWhen.key];
    const expectedValue = field.showWhen.value;

    if (Array.isArray(expectedValue)) {
      return expectedValue.includes(currentValue);
    }

    return currentValue === expectedValue;
  };

  return (
    <div className="admin-modal-overlay">
      <div className={`admin-modal ${size || "md"} ${modalClassName}`}>
        <div className="admin-modal-header">
          <div className="admin-modal-title">
            <h5>{`${mode.charAt(0).toUpperCase() + mode.slice(1)} ${title}`}</h5>
          </div>

          <button onClick={onClose} className="close-btn">
            ✕
          </button>
        </div>

        <div className={`admin-modal-body ${bodyClassName}`}>
          {fields.map((col) => {
            if (!shouldShowField(col)) {
              return null;
            }
            const isDisabled = mode === "view" || col.disabled;

            // ===== PERMISSION MATRIX =====
            if (col.type === "permission-matrix") {
              return (
                <div
                  key={col.key}
                  className={`admin-form-group col-span-${col.colSpan ?? 2}`}
                >
                  <label>{col.label}</label>

                  <table className="permission-table">
                    <thead>
                      <tr>
                        <th>Module</th>

                        {["view", "create", "edit", "delete"].map((perm) => (
                          <th key={perm}>
                            <div className="permission-header">
                              <div>
                                {perm.charAt(0).toUpperCase() + perm.slice(1)}
                              </div>

                              <input
                                type="checkbox"
                                disabled={isDisabled}
                                onChange={(e) =>
                                  toggleColumnPermission(
                                    col.key,
                                    perm,
                                    e.target.checked,
                                    col.options || [],
                                  )
                                }
                              />
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {col.options?.map((module: any) => {
                        const modulePermissions =
                          formData[col.key]?.[module.value] || {};

                        return (
                          <tr key={module.value}>
                            <td>{module.label}</td>

                            {["view", "create", "edit", "delete"].map(
                              (perm) => (
                                <td key={perm}>
                                  <input
                                    type="checkbox"
                                    checked={modulePermissions[perm] || false}
                                    onChange={(e) => {
                                      const current = formData[col.key] || {};
                                      const moduleData =
                                        current[module.value] || {};

                                      const nextFormData = {
                                        ...formData,
                                        [col.key]: {
                                          ...current,
                                          [module.value]: {
                                            ...moduleData,
                                            [perm]: e.target.checked,
                                          },
                                        },
                                      };

                                      setFormData(nextFormData);
                                    }}
                                    disabled={isDisabled}
                                  />
                                </td>
                              ),
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }

            // ===== CHECKBOX GROUP =====
            if (col.type === "checkbox-group") {
              return (
                <div
                  key={col.key}
                  className={`admin-form-group col-span-${col.colSpan ?? 2}`}
                >
                  <label>{col.label}</label>

                  <div className="checkbox-group">
                    {col.options?.map((opt: any) => (
                      <label key={opt.value} className="checkbox-item">
                        <input
                          type="checkbox"
                          value={opt.value}
                          checked={
                            formData[col.key]?.includes(opt.value) || false
                          }
                          onChange={() =>
                            handleCheckboxChange(col.key, opt.value)
                          }
                          disabled={isDisabled}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            }

            // ===== ID =====
            if (col.key.toLowerCase() === "id") {
              return (
                <div
                  key={col.key}
                  className={`admin-form-group col-span-${col.colSpan ?? 2}`}
                >
                  <label>{col.label}</label>
                  <input
                    type="text"
                    value={
                      mode === "add"
                        ? "Auto Generated"
                        : formData[col.key] || ""
                    }
                    disabled
                  />
                </div>
              );
            }

            // ===== STATUS =====
            if (
              col.key.toLowerCase() === "status" &&
              col.options &&
              col.options.length === 2 &&
              col.options.some((opt) => opt.value === "active") &&
              col.options.some((opt) => opt.value === "inactive")
            ) {
              const currentStatus = (
                formData[col.key] || "active"
              ).toLowerCase();

              return (
                <div key={col.key} className="admin-form-group">
                  <label>{col.label}</label>
                  <div className="status-radio-group">
                    <label>
                      <input
                        type="radio"
                        checked={currentStatus === "active"}
                        onChange={() => handleChange(col.key, "active")}
                        disabled={isDisabled}
                      />
                      Active
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={currentStatus === "inactive"}
                        onChange={() => handleChange(col.key, "inactive")}
                        disabled={isDisabled}
                      />
                      Inactive
                    </label>
                  </div>
                </div>
              );
            }

            // ===== IMAGE =====
            if (col.type === "image") {
              // 🔹 Single Image
              if (col.imageType === "single") {
                const inputId = `${col.key}-single`;
                const imageData = formData[col.key];

                return (
                  <div key={col.key} className="admin-form-group">
                    <label>{col.label}</label>

                    <div
                      className="image-upload-box"
                      onClick={() => {
                        if (!isDisabled)
                          document.getElementById(inputId)?.click();
                      }}
                    >
                      {imageData?.preview && !imageData?.removed ? (
                        <>
                          <img
                            src={imageData.preview}
                            className="preview-image"
                          />

                          {!isDisabled && (
                            <>
                              <span
                                className="download-image-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(imageData, col.label);
                                }}
                              >
                                <DownloadIcon />
                              </span>

                              <span
                                className="remove-image-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  askRemoveSingleImage(col.key);
                                }}
                              >
                                ✕
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <span>Click to Upload</span>
                      )}
                    </div>

                    <input
                      id={inputId}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      disabled={isDisabled}
                      onChange={(e) =>
                        handleImageChange(col.key, e.target.files)
                      }
                    />
                  </div>
                );
              }

              // 🔹 Product Images (Main + 5)
              if (col.imageType === "product") {
                const images = formData[col.key] || [];

                return (
                  <div key={col.key} className="admin-form-group">
                    <label>{col.label}</label>

                    <div className="product-images-grid">
                      {[0, 1, 2, 3, 4, 5].map((index) => {
                        const img = images[index];
                        const inputId = `${col.key}-${index}`;

                        return (
                          <div key={index} className="image-wrapper">
                            <div
                              className="image-slot"
                              onClick={() => {
                                if (!isDisabled)
                                  document.getElementById(inputId)?.click();
                              }}
                            >
                              {img?.preview ? (
                                <>
                                  <img
                                    src={img.preview}
                                    className="preview-image"
                                  />

                                  {!isDisabled && (
                                    <>
                                      <span
                                        className="download-image-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          downloadImage(img, `image-${index}`);
                                        }}
                                      >
                                        <DownloadIcon />
                                      </span>

                                      <span
                                        className="remove-image-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          askRemoveProductImage(col.key, index);
                                        }}
                                      >
                                        ✕
                                      </span>
                                    </>
                                  )}
                                </>
                              ) : (
                                <span>
                                  {index === 0 ? "Main" : `Image ${index}`}
                                </span>
                              )}
                            </div>

                            <input
                              id={inputId}
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              disabled={isDisabled}
                              onChange={(e) =>
                                handleProductImageChange(
                                  col.key,
                                  index,
                                  e.target.files,
                                )
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
            }

            // ===== VIDEO =====

            if (col.type === "video") {
              const inputId = `${col.key}-video`;
              const videoData = formData[col.key];

              return (
                <div key={col.key} className="admin-form-group">
                  <label>{col.label}</label>

                  <div
                    className="video-upload-box"
                    onClick={() => {
                      if (!isDisabled)
                        document.getElementById(inputId)?.click();
                    }}
                  >
                    {videoData?.preview ? (
                      <>
                        <video
                          src={videoData.preview}
                          className="preview-video"
                          controls
                        />

                        {!isDisabled && (
                          <>
                            <span
                              className="download-video-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadImage(
                                  videoData,
                                  videoData.fileName || "product-video",
                                );
                              }}
                            >
                              <DownloadIcon />
                            </span>

                            <span
                              className="remove-video-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChange(col.key, "");
                              }}
                            >
                              ✕
                            </span>
                          </>
                        )}
                      </>
                    ) : (
                      <span>Click to Upload Video</span>
                    )}
                  </div>

                  <input
                    id={inputId}
                    type="file"
                    accept="video/*"
                    hidden
                    disabled={isDisabled}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      handleChange(col.key, {
                        file,
                        fileName: file.name,
                        preview: URL.createObjectURL(file),
                      });
                    }}
                  />
                </div>
              );
            }

            // ===== COLOR VARIANTS =====
            if (col.type === "color-variants") {
              const variants =
                Array.isArray(formData[col.key]) && formData[col.key].length > 0
                  ? formData[col.key]
                  : [{ color: "", images: [null, null, null] }];
              return (
                <div key={col.key} className="admin-form-group col-span-2">
                  <label>{col.label}</label>

                  <div className="color-variants-wrap">
                    {variants.map((variant: any, index: number) => (
                      <div key={index} className="color-variant-block">
                        {/* ===== HEADER ===== */}
                        <div className="color-variant-header">
                          <input
                            type="text"
                            placeholder="Enter color"
                            value={variant.color || ""}
                            onChange={(e) => {
                              const updated = [...variants];
                              updated[index].color = e.target.value;
                              handleChange(col.key, updated);
                            }}
                          />

                          <button
                            type="button"
                            className="variant-add-btn"
                            onClick={() => {
                              const updated = [...variants];
                              updated.push({
                                color: "",
                                images: [null, null, null],
                              });
                              handleChange(col.key, updated);
                            }}
                          >
                            +
                          </button>

                          {index > 0 && (
                            <button
                              type="button"
                              className="variant-delete-btn"
                              onClick={() => {
                                const updated = [...variants];
                                updated.splice(index, 1);
                                handleChange(col.key, updated);
                              }}
                            >
                              🗑
                            </button>
                          )}
                        </div>

                        {/* ===== IMAGES ===== */}
                        <div className="variant-images-grid">
                          {[0, 1, 2].map((imgIndex) => {
                            const img = variant.images?.[imgIndex];
                            const inputId = `${col.key}-${index}-${imgIndex}`;

                            return (
                              <div key={imgIndex} className="image-wrapper">
                                <div
                                  className="image-slot"
                                  onClick={() => {
                                    if (!isDisabled) {
                                      document.getElementById(inputId)?.click();
                                    }
                                  }}
                                >
                                  {img?.preview ? (
                                    <>
                                      <img
                                        src={img.preview}
                                        className="preview-image"
                                      />

                                      {!isDisabled && (
                                        <>
                                          <span
                                            className="download-image-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              downloadImage(
                                                img,
                                                `color-${index + 1}-image-${imgIndex + 1}`,
                                              );
                                            }}
                                          >
                                            <DownloadIcon />
                                          </span>

                                          <span
                                            className="remove-image-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              askRemoveColorVariantImage(
                                                col.key,
                                                index,
                                                imgIndex,
                                              );
                                            }}
                                          >
                                            ✕
                                          </span>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <span>Image {imgIndex + 1}</span>
                                  )}
                                </div>

                                <input
                                  id={inputId}
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  disabled={isDisabled}
                                  onChange={(e) =>
                                    handleColorVariantImageChange(
                                      col.key,
                                      index,
                                      imgIndex,
                                      e.target.files,
                                    )
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            if (col.type === "readonly-text") {
              return (
                <div key={col.key} className="admin-form-group">
                  <label>{col.label}</label>
                  <div className="admin-readonly-text">
                    {formData[col.key] || "-"}
                  </div>
                </div>
              );
            }

            if (col.type === "repeatable-textarea") {
              const values = Array.isArray(formData[col.key])
                ? formData[col.key]
                : formData[col.key]
                  ? [formData[col.key]]
                  : [""];

              return (
                <div key={col.key} className="admin-form-group">
                  <label>{col.label}</label>

                  {values.map((item: string, index: number) => (
                    <div key={index} className="admin-repeatable-row">
                      <textarea
                        value={item || ""}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const updated = [...values];
                          updated[index] = e.target.value;
                          handleChange(col.key, updated);
                        }}
                      />

                      {!isDisabled && values.length > 1 && (
                        <button
                          type="button"
                          className="admin-repeatable-remove-btn"
                          onClick={() => {
                            const updated = values.filter(
                              (_: any, i: number) => i !== index,
                            );
                            handleChange(col.key, updated);
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}

                  {!isDisabled && (
                    <button
                      type="button"
                      className="admin-repeatable-add-btn"
                      onClick={() => handleChange(col.key, [...values, ""])}
                    >
                      + Add
                    </button>
                  )}
                </div>
              );
            }

            if (col.type === "repeatable-faq") {
              const values = Array.isArray(formData[col.key])
                ? formData[col.key]
                : [{ question: "", answer: "" }];

              return (
                <div key={col.key} className="admin-form-group">
                  <label>{col.label}</label>

                  {values.map((item: any, index: number) => (
                    <div key={index} className="admin-faq-repeatable-box">
                      <input
                        type="text"
                        placeholder="Question"
                        value={item.question || ""}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const updated = [...values];
                          updated[index] = {
                            ...updated[index],
                            question: e.target.value,
                          };
                          handleChange(col.key, updated);
                        }}
                      />

                      <textarea
                        placeholder="Answer"
                        value={item.answer || ""}
                        disabled={isDisabled}
                        onChange={(e) => {
                          const updated = [...values];
                          updated[index] = {
                            ...updated[index],
                            answer: e.target.value,
                          };
                          handleChange(col.key, updated);
                        }}
                      />

                      {!isDisabled && values.length > 1 && (
                        <button
                          type="button"
                          className="admin-repeatable-remove-btn"
                          onClick={() => {
                            const updated = values.filter(
                              (_: any, i: number) => i !== index,
                            );
                            handleChange(col.key, updated);
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}

                  {!isDisabled && (
                    <button
                      type="button"
                      className="admin-repeatable-add-btn"
                      onClick={() =>
                        handleChange(col.key, [
                          ...values,
                          { question: "", answer: "" },
                        ])
                      }
                    >
                      + Add FAQ
                    </button>
                  )}
                </div>
              );
            }

            if (col.type === "repeatable-solution") {
              const values = Array.isArray(formData[col.key])
                ? formData[col.key]
                : [{ title: "", image: null }];

              const folder = col.imageFolder || "software-support";

              return (
                <div
                  key={col.key}
                  className={`admin-form-group col-span-${col.colSpan ?? 2}`}
                >
                  <label>{col.label}</label>

                  {values.map((item: any, index: number) => {
                    const inputId = `${col.key}-solution-image-${index}`;

                    const preview =
                      item.image?.preview ||
                      (typeof item.image === "string" && item.image
                        ? `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/${folder}/${item.image}`
                        : "");

                    return (
                      <div key={index} className="admin-solution-card">
                        <div className="admin-solution-card-header">
                          <span>Solution #{index + 1}</span>

                          {!isDisabled && values.length > 1 && (
                            <button
                              type="button"
                              className="admin-repeatable-remove-btn"
                              onClick={() => {
                                const updated = values.filter(
                                  (_: any, i: number) => i !== index,
                                );
                                handleChange(col.key, updated);
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>

                        <input
                          type="text"
                          placeholder="Solution Title"
                          value={item.title || ""}
                          disabled={isDisabled}
                          onChange={(e) => {
                            const updated = [...values];
                            updated[index] = {
                              ...updated[index],
                              title: e.target.value,
                            };
                            handleChange(col.key, updated);
                          }}
                        />

                        <div
                          className="image-upload-box solution-image-upload-box"
                          onClick={() => {
                            if (!isDisabled) {
                              document.getElementById(inputId)?.click();
                            }
                          }}
                        >
                          {preview ? (
                            <>
                              <img src={preview} className="preview-image" />

                              {!isDisabled && (
                                <>
                                  <span
                                    className="download-image-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadImage(
                                        { preview },
                                        `solution-${index + 1}`,
                                      );
                                    }}
                                  >
                                    <DownloadIcon />
                                  </span>

                                  <span
                                    className="remove-image-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      const updated = [...values];
                                      updated[index] = {
                                        ...updated[index],
                                        image: null,
                                      };

                                      handleChange(col.key, updated);
                                    }}
                                  >
                                    ✕
                                  </span>
                                </>
                              )}
                            </>
                          ) : (
                            <span>Click to Upload</span>
                          )}
                        </div>

                        <input
                          id={inputId}
                          type="file"
                          accept="image/*"
                          hidden
                          disabled={isDisabled}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            const updated = [...values];

                            updated[index] = {
                              ...updated[index],
                              image: {
                                file,
                                preview: URL.createObjectURL(file),
                              },
                            };

                            handleChange(col.key, updated);
                          }}
                        />
                      </div>
                    );
                  })}

                  {!isDisabled && (
                    <button
                      type="button"
                      className="admin-repeatable-add-btn"
                      onClick={() =>
                        handleChange(col.key, [
                          ...values,
                          { title: "", image: null },
                        ])
                      }
                    >
                      + Add
                    </button>
                  )}
                </div>
              );
            }

            if (col.type === "repeatable-card-textarea") {
              const values = Array.isArray(formData[col.key])
                ? formData[col.key]
                : formData[col.key]
                  ? [formData[col.key]]
                  : [""];

              return (
                <div
                  key={col.key}
                  className={`admin-form-group col-span-${col.colSpan ?? 2}`}
                >
                  <label>{col.label}</label>

                  <div className="admin-card-repeatable-wrap">
                    {values.map((item: string, index: number) => (
                      <div key={index} className="admin-card-repeatable-item">
                        <div className="admin-card-repeatable-header">
                          <span>Item #{index + 1}</span>

                          {!isDisabled && values.length > 1 && (
                            <button
                              type="button"
                              className="admin-card-remove-btn"
                              onClick={() => {
                                const updated = values.filter(
                                  (_: any, i: number) => i !== index,
                                );
                                handleChange(col.key, updated);
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>

                        <textarea
                          value={item || ""}
                          disabled={isDisabled}
                          placeholder="Enter content"
                          onChange={(e) => {
                            const updated = [...values];
                            updated[index] = e.target.value;
                            handleChange(col.key, updated);
                          }}
                        />
                      </div>
                    ))}

                    {!isDisabled && (
                      <button
                        type="button"
                        className="admin-card-add-btn"
                        onClick={() => handleChange(col.key, [...values, ""])}
                      >
                        + Add
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            if (col.type === "repeatable-card-faq") {
              const values = Array.isArray(formData[col.key])
                ? formData[col.key]
                : [{ question: "", answer: "" }];

              return (
                <div
                  key={col.key}
                  className={`admin-form-group col-span-${col.colSpan ?? 2}`}
                >
                  <label>{col.label}</label>

                  <div className="admin-card-repeatable-wrap">
                    {values.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="admin-card-repeatable-item admin-faq-card-item"
                      >
                        <div className="admin-card-repeatable-header">
                          <span>FAQ #{index + 1}</span>

                          {!isDisabled && values.length > 1 && (
                            <button
                              type="button"
                              className="admin-card-remove-btn"
                              onClick={() => {
                                const updated = values.filter(
                                  (_: any, i: number) => i !== index,
                                );
                                handleChange(col.key, updated);
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>

                        <input
                          type="text"
                          placeholder="Question"
                          value={item.question || ""}
                          disabled={isDisabled}
                          onChange={(e) => {
                            const updated = [...values];
                            updated[index] = {
                              ...updated[index],
                              question: e.target.value,
                            };
                            handleChange(col.key, updated);
                          }}
                        />

                        <textarea
                          placeholder="Answer"
                          value={item.answer || ""}
                          disabled={isDisabled}
                          onChange={(e) => {
                            const updated = [...values];
                            updated[index] = {
                              ...updated[index],
                              answer: e.target.value,
                            };
                            handleChange(col.key, updated);
                          }}
                        />
                      </div>
                    ))}

                    {!isDisabled && (
                      <button
                        type="button"
                        className="admin-card-add-btn"
                        onClick={() =>
                          handleChange(col.key, [
                            ...values,
                            { question: "", answer: "" },
                          ])
                        }
                      >
                        + Add FAQ
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            if (col.type === "repeatable-combo-list") {
              const values = Array.isArray(formData[col.key])
                ? formData[col.key]
                : [];

              const inputKey = `${col.key}_input`;
              const editIndexKey = `${col.key}_edit_index`;

              const inputValue = formData[inputKey] || "";
              const editIndex =
                formData[editIndexKey] !== undefined &&
                formData[editIndexKey] !== null
                  ? Number(formData[editIndexKey])
                  : -1;

              const isEditing = editIndex >= 0;

              const resetComboInput = () => {
                handleChange(inputKey, "");
                handleChange(editIndexKey, -1);
              };

              const addOrUpdateItem = () => {
                const text = String(inputValue || "").trim();
                if (!text) return;

                if (isEditing) {
                  const updated = [...values];
                  updated[editIndex] = text;
                  handleChange(col.key, updated);
                  resetComboInput();
                  return;
                }

                handleChange(col.key, [...values, text]);
                resetComboInput();
              };

              return (
                <div
                  key={col.key}
                  className={`admin-form-group col-span-${col.colSpan ?? 2}`}
                >
                  <label>{col.label}</label>

                  {!isDisabled && (
                    <div className="admin-combo-input-row">
                      <input
                        type="text"
                        value={inputValue}
                        placeholder="Enter problem"
                        onChange={(e) => handleChange(inputKey, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addOrUpdateItem();
                          }

                          if (e.key === "Escape") {
                            resetComboInput();
                          }
                        }}
                      />

                      <button
                        type="button"
                        className={`admin-combo-add-small-btn ${
                          isEditing ? "is-update" : ""
                        }`}
                        onClick={addOrUpdateItem}
                      >
                        {isEditing ? "Update" : "+"}
                      </button>
                    </div>
                  )}

                  <div className="admin-combo-list-box">
                    {values.length === 0 ? (
                      <div className="admin-combo-empty">
                        No problems added yet.
                      </div>
                    ) : (
                      values.map((item: string, index: number) => (
                        <div
                          key={index}
                          className={`admin-combo-list-item ${
                            editIndex === index ? "is-editing" : ""
                          }`}
                          onDoubleClick={() => {
                            if (isDisabled) return;

                            handleChange(inputKey, item);
                            handleChange(editIndexKey, index);
                          }}
                          title="Double click to edit"
                        >
                          <span>{item}</span>

                          {!isDisabled && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = values.filter(
                                  (_: any, i: number) => i !== index,
                                );

                                handleChange(col.key, updated);

                                if (editIndex === index) {
                                  resetComboInput();
                                }
                              }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            }

            // ===== NORMAL FIELD =====
            return (
              <div key={col.key} className="admin-form-group">
                <label>{col.label}</label>

                {col.type === "textarea" ? (
                  <textarea
                    value={formData[col.key] || ""}
                    onChange={(e) => handleChange(col.key, e.target.value)}
                    disabled={isDisabled}
                  />
                ) : col.type === "multiselect" ? (
                  <>
                    {(() => {
                      const selectedValues = Array.isArray(formData[col.key])
                        ? formData[col.key].map((value: any) => Number(value))
                        : [];

                      const searchValue = multiSelectSearch[col.key] || "";

                      const filteredOptions = (col.options || []).filter(
                        (option: any) =>
                          String(option.label || "")
                            .toLowerCase()
                            .includes(searchValue.toLowerCase()),
                      );

                      return (
                        <>
                          <div className="admin-checkbox-multiselect-search-wrap">
                            <input
                              type="text"
                              className="admin-checkbox-multiselect-search"
                              placeholder="Search..."
                              value={searchValue}
                              disabled={isDisabled}
                              onChange={(e) =>
                                setMultiSelectSearch((prev) => ({
                                  ...prev,
                                  [col.key]: e.target.value,
                                }))
                              }
                            />

                            <span className="admin-checkbox-multiselect-count">
                              {selectedValues.length} Selected
                            </span>
                          </div>

                          <div className="admin-checkbox-multiselect">
                            {filteredOptions.length > 0 ? (
                              filteredOptions.map((option: any) => {
                                const optionValue = Number(option.value);
                                const isChecked =
                                  selectedValues.includes(optionValue);

                                return (
                                  <label
                                    key={option.value}
                                    className="admin-checkbox-multiselect-item"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      disabled={isDisabled}
                                      onChange={(e) => {
                                        let updatedValues = [...selectedValues];

                                        if (e.target.checked) {
                                          updatedValues.push(optionValue);
                                        } else {
                                          updatedValues = updatedValues.filter(
                                            (value) => value !== optionValue,
                                          );
                                        }

                                        handleChange(col.key, updatedValues);
                                      }}
                                    />

                                    <span>{option.label}</span>
                                  </label>
                                );
                              })
                            ) : (
                              <div className="admin-checkbox-multiselect-empty">
                                No matching options found.
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}

                    {!isDisabled &&
                      Array.isArray(formData[col.key]) &&
                      formData[col.key].length > 0 && (
                        <button
                          type="button"
                          className="admin-clear-multiselect-btn"
                          onClick={() => handleChange(col.key, [])}
                        >
                          Clear Selected
                        </button>
                      )}
                  </>
                ) : col.type === "select" ? (
                  <select
                    value={
                      formData[col.key] !== undefined &&
                      formData[col.key] !== null
                        ? formData[col.key]
                        : ""
                    }
                    onChange={(e) => {
                      const selected = e.target.value;

                      const selectedOption =
                        col.options?.find(
                          (opt) => String(opt.value) === selected,
                        ) || null;

                      const parsedValue = selectedOption?.value ?? selected;

                      if (col.key === "categoryId") {
                        setFormData((prev: any) => ({
                          ...prev,
                          categoryId: parsedValue,
                          subcategoryId: "",
                        }));
                      } else if (col.key === "vendor_id") {
                        setFormData((prev: any) => ({
                          ...prev,
                          vendor_id: parsedValue,
                          region_id: selectedOption?.region_id ?? "",
                          region_name: selectedOption?.region_name ?? "",
                        }));
                      } else {
                        handleChange(col.key, parsedValue);
                      }
                    }}
                    disabled={isDisabled}
                  >
                    <option value="">Select</option>
                    {col.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : col.type === "currency" ? (
                  <input
                    type="text"
                    value={formData[col.key] || ""}
                    onFocus={(e) => {
                      const raw = String(e.target.value || "").replace(
                        /,/g,
                        "",
                      );
                      handleChange(col.key, raw);
                    }}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "");
                      handleChange(col.key, raw);
                    }}
                    onBlur={(e) => {
                      const raw = String(e.target.value || "").replace(
                        /,/g,
                        "",
                      );
                      const num = Number(raw);

                      if (raw === "" || isNaN(num)) {
                        handleChange(col.key, "");
                        return;
                      }

                      handleChange(
                        col.key,
                        num.toLocaleString("en-MY", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }),
                      );
                    }}
                    disabled={isDisabled}
                  />
                ) : (
                  <input
                    type={col.type || "text"}
                    value={formData[col.key] || ""}
                    onChange={(e) => handleChange(col.key, e.target.value)}
                    disabled={isDisabled}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="admin-modal-footer">
          {footer ? (
            footer
          ) : (
            <>
              <button onClick={onClose} className="btn-secondary">
                Cancel
              </button>

              {!hideDefaultSubmit && mode !== "view" && (
                <button onClick={handleSubmit} className="btn-primary">
                  {mode === "add" ? "Create" : "Update"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDataModal;
